import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { axiosClient, formatError, getBaseUrlVersioned } from "../../services/axiosClient";
import { ABORT_ERROR_MESSAGE } from "./constants";
import conversationLogger from "./conversationLogger";
import { ConversationFlow } from "./types";
export const getCharacterMessagesThunk = createAsyncThunk("messages/getById", async ({ character_id, conversation_id, last_id, limit = 50 }, thunkAPI) => {
    try {
        const baseUrl = `${getBaseUrlVersioned(null)}/characters/${character_id}/messages`;
        const urlPath = conversation_id ? `${baseUrl}/${conversation_id}` : baseUrl;
        const params = new URLSearchParams({ limit: limit.toString() });
        if (last_id) {
            params.append("last_id", last_id);
        }
        const apiUrl = `${urlPath}?${params.toString()}`;
        const response = await axiosClient.get(apiUrl);
        return response.data;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const updateMessage = createAsyncThunk("messages/update", async ({ id, content }, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(null)}/messages/${id}`;
        const response = await axiosClient.patch(apiUrl, { content });
        return response.data;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const flagMessage = createAsyncThunk("event", async (param) => {
    try {
        const apiUrl = getBaseUrlVersioned("event");
        const response = await axiosClient.post(apiUrl, param);
        return response.data;
    }
    catch (error) {
        conversationLogger.error(`Error flagging message with param '${JSON.stringify(param)}'\nreceived: ${formatError(error)}`);
        throw error;
    }
});
export const deleteMessage = createAsyncThunk("message/delete", async (param) => {
    try {
        const apiUrl = getBaseUrlVersioned(`conversations/${param.conversation_id}/messages`);
        const response = await axiosClient.delete(apiUrl, {
            data: { ids: param.ids },
        });
        return response.data;
    }
    catch (error) {
        conversationLogger.error(`Error deleting message with param '${JSON.stringify(param)}'\nreceived: ${formatError(error)}`);
        throw error;
    }
});
export const sendEventThunk = createAsyncThunk("sendEvent", async (param) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(null)}/event`;
        const response = await axiosClient.post(apiUrl, param);
        return response.data;
    }
    catch (error) {
        conversationLogger.error(`Error event with param '${JSON.stringify(param)}'\nreceived: ${formatError(error)}`);
        throw error;
    }
});
export const generateMessage = createAsyncThunk("conversation/generateMessage", async (param, thunkAPI) => {
    try {
        const { abortSignal, ...requestPayload } = param;
        const response = await axiosClient.post(getBaseUrlVersioned("chat"), requestPayload, {
            signal: abortSignal,
        });
        return response.data;
    }
    catch (err) {
        if (axios.isCancel(err)) {
            throw new Error(ABORT_ERROR_MESSAGE);
        }
        throw thunkAPI.rejectWithValue(err?.response?.data);
    }
});
export const generateConversationImage = createAsyncThunk("conversation/generateConversationImage", async (param, thunkAPI) => {
    try {
        const response = await axiosClient.post(`${getBaseUrlVersioned("chat/conversation-image")}`, param);
        return response.data;
    }
    catch (err) {
        throw thunkAPI.rejectWithValue(err?.response?.data);
    }
});
export const uploadConversationImage = createAsyncThunk("conversation/uploadConversationImage", async (params, thunkAPI) => {
    try {
        const { abortSignal, ...payload } = params;
        const apiUrl = `${getBaseUrlVersioned(null)}/chat/user-image`;
        const response = await axiosClient.post(apiUrl, payload, {
            signal: abortSignal,
        });
        return response.data;
    }
    catch (err) {
        if (axios.isCancel(err)) {
            throw new Error(ABORT_ERROR_MESSAGE);
        }
        const error = err;
        const responseData = error?.response?.data;
        throw thunkAPI.rejectWithValue({
            error: responseData?.error ?? error?.message,
            errorCode: responseData?.errorCode,
            message: error?.message,
        });
    }
});
export const deleteConversationImage = createAsyncThunk("conversation/deleteConversationImage", async (params, thunkAPI) => {
    try {
        const { imageId, ...payload } = params;
        const apiUrl = `${getBaseUrlVersioned(null)}/chat/user-image/${imageId}`;
        const response = await axiosClient.delete(apiUrl, {
            data: payload,
        });
        return response.data;
    }
    catch (err) {
        const error = err;
        const responseData = error?.response?.data;
        throw thunkAPI.rejectWithValue({
            error: responseData?.error ?? error?.message,
            errorCode: responseData?.errorCode,
            message: error?.message,
        });
    }
});
export const rateMessage = createAsyncThunk("conversation/rateMessage", async (params, thunkAPI) => {
    try {
        const { messageId, label } = params;
        const apiUrl = `${getBaseUrlVersioned(null)}/messages/${messageId}/label`;
        const payload = { message_id: messageId, label };
        const response = await axiosClient.post(apiUrl, payload);
        return response.data;
    }
    catch (err) {
        throw thunkAPI.rejectWithValue(err?.response?.data);
    }
});
export const deleteMessageImage = createAsyncThunk("messages/deleteImage", async (messageId, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(null)}/messages/${messageId}/image`;
        const response = await axiosClient.delete(apiUrl);
        return response.data;
    }
    catch (err) {
        throw thunkAPI.rejectWithValue(err?.response?.data);
    }
});
const initialState = {
    selectedMessageIndex: -1,
    isImageUploading: false,
    isLoading: false,
    loadingType: null,
};
export const conversationSlice = createSlice({
    name: "conversation",
    initialState,
    reducers: {
        setSelectedMessageIndex: (state, action) => {
            state.selectedMessageIndex = action.payload;
        },
        resetConversationImageUploadState: (state) => {
            state.isImageUploading = false;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(updateMessage.pending, (state) => {
            state.isLoading = true;
            state.loadingType = ConversationFlow.UPDATE_MESSAGE;
        });
        builder.addCase(updateMessage.fulfilled, (state) => {
            state.isLoading = false;
            state.loadingType = null;
        });
        builder.addCase(updateMessage.rejected, (state) => {
            state.isLoading = false;
            state.loadingType = null;
        });
        builder.addCase(deleteMessageImage.pending, (state) => {
            state.isLoading = true;
            state.loadingType = ConversationFlow.DELETE_MESSAGE_IMAGE;
        });
        builder.addCase(deleteMessageImage.fulfilled, (state) => {
            state.isLoading = false;
            state.loadingType = null;
        });
        builder.addCase(deleteMessageImage.rejected, (state) => {
            state.isLoading = false;
            state.loadingType = null;
        });
        builder.addCase(uploadConversationImage.pending, (state) => {
            state.isImageUploading = true;
        });
        builder.addCase(uploadConversationImage.fulfilled, (state) => {
            state.isImageUploading = false;
        });
        builder.addCase(uploadConversationImage.rejected, (state) => {
            state.isImageUploading = false;
        });
    },
});
export const { resetConversationImageUploadState } = conversationSlice.actions;
export default conversationSlice.reducer;
